import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@/config/Grid/Grid";
import Button from "@/components/Button/Button";
import IconButton from "@/components/IconButton/IconButton";
import TransparentTile from "@/components/TransparentTile/TransparentTile";
import ItemToPayComponent from "@/components/ItemToPayComponent/ItemToPayComponent";
import OrderActionBar from "@/layouts/Order/components/OrderActionBar";
import LoyaltyQrCode from "@/components/LoyaltyQrCode/LoyaltyQrCode";
import { useMenuData } from "@/hooks/useMenuData";
import { useGoBack } from "@/hooks/useGoBack";
import { getTranslation } from "@/helpers/getTranslation";
import { generateId } from "@/helpers/generateId";
import {
  CONFIGURED_REWARDS,
  REWARD_FALLBACK,
  type LoyaltyRewardConfig,
} from "@/config/loyalty";
import {
  useLoyaltyActions,
  useLoyaltyAvailablePoints,
  useLoyaltyHashes,
  useLoyaltyPending,
  useLoyaltyPoints,
} from "@/store/useLoyaltyStore";
import { useCartActions } from "@/store/useCartStore";
import { billsPath, menuDishPath, orderPaths } from "@/routes/paths";
import type { Dish } from "@/api/Dishes/DishesAPI.model";
import styles from "./Loyalty.module.scss";

interface RedeemableReward extends LoyaltyRewardConfig {
  dish: Dish;
}

function LoyaltyPage() {
  const { restaurantId = "", tableId = "" } = useParams<{
    restaurantId: string;
    tableId: string;
  }>();
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const navigate = useNavigate();
  const goBack = useGoBack(billsPath(restaurantId, tableId));

  const { dishes } = useMenuData(restaurantId);
  const points = useLoyaltyPoints();
  const availablePoints = useLoyaltyAvailablePoints();
  const pending = useLoyaltyPending();
  const hashes = useLoyaltyHashes();
  const {
    redeem,
    startRedemption,
    settleRedemption,
    cancelRedemption,
    releaseExpiredRedemption,
    expireIfStale,
  } = useLoyaltyActions();
  const { addLine } = useCartActions();

  /** A guest away longer than the validity window starts from zero. */
  useEffect(() => {
    expireIfStale();
  }, [expireIfStale]);

  /**
   * An open code expires on its own, so the screen has to notice. Checking
   * every fifteen seconds is enough for a countdown shown in whole minutes.
   */
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    releaseExpiredRedemption();
    const timer = setInterval(() => {
      setNow(Date.now());
      releaseExpiredRedemption();
    }, 15000);
    return () => clearInterval(timer);
  }, [releaseExpiredRedemption]);

  useEffect(() => {
    if (pending) setShowCode(true);
  }, [pending]);

  const minutesLeft = pending
    ? Math.max(
        0,
        Math.ceil((new Date(pending.expiresAt).getTime() - now) / 60000),
      )
    : 0;

  const rewards = useMemo<RedeemableReward[]>(() => {
    const byId = new Map(dishes.map((dish) => [dish.id, dish]));

    if (CONFIGURED_REWARDS.length > 0) {
      return CONFIGURED_REWARDS.filter((reward) => reward.isActive).flatMap(
        (reward) => {
          // A reward whose dish left the menu is dropped, not shown blank.
          const dish = reward.dishIds
            .map((dishId) => byId.get(dishId))
            .find(Boolean);
          return dish ? [{ ...reward, dish }] : [];
        },
      );
    }

    // No configured programme: offer the cheapest dishes on the live menu, so
    // every reward is something the kitchen actually makes.
    return [...dishes]
      .sort((a, b) => a.price - b.price)
      .slice(0, REWARD_FALLBACK.count)
      .map((dish) => ({
        id: `fallback-${dish.id}`,
        dishIds: [dish.id],
        points: REWARD_FALLBACK.points,
        isActive: true,
        dish,
      }));
  }, [dishes]);

  /**
   * Which rewards the guest has ticked. `listVersion` remounts the tiles after
   * a claim: ItemToPayComponent keeps its own checked state, so clearing the
   * selection means giving it fresh instances.
   */
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  /**
   * Whether the open code is on screen. Stepping back from it returns to the
   * reward list without surrendering the reservation, and a newly issued code
   * always comes to the front.
   */
  const [showCode, setShowCode] = useState(true);

  const [listVersion, setListVersion] = useState(0);

  const setQuantity = (rewardId: string, quantity: number) =>
    setQuantities((current) => ({ ...current, [rewardId]: quantity }));

  const clearSelection = () => {
    setQuantities({});
    setListVersion((version) => version + 1);
  };

  /** Each pick expanded to one entry per unit, so two of a dish is two rows. */
  const selectedUnits = rewards.flatMap((reward) =>
    Array.from({ length: quantities[reward.id] ?? 0 }, () => reward),
  );
  const selectedPoints = selectedUnits.reduce(
    (sum, reward) => sum + reward.points,
    0,
  );
  const canClaim = selectedUnits.length > 0 && selectedPoints <= availablePoints;

  /**
   * How many of a reward the guest can still afford: what they already picked,
   * plus whatever the points left over will buy.
   */
  const maxQuantityFor = (reward: RedeemableReward) => {
    const picked = quantities[reward.id] ?? 0;
    const spentOnOthers = selectedPoints - picked * reward.points;
    const spare = Math.max(0, availablePoints - spentOnOthers);
    return Math.floor(spare / reward.points);
  };

  const handleGenerateCode = () => {
    if (!canClaim) return;

    // What the waiter's scanner reads: who, what, and which redemption.
    const code = [
      "menu-loyalty",
      hashes[0] ?? "anon",
      selectedUnits.map((reward) => reward.dish.id).join(","),
      selectedPoints,
      generateId(),
    ].join(":");

    // Reserves the points rather than spending them. They leave the balance
    // when the reward is actually handed over, and come back if the code runs
    // out first.
    startRedemption({
      code,
      dishIds: selectedUnits.map((reward) => reward.dish.id),
      points: selectedPoints,
    });
    clearSelection();
  };

  const handleOrderThroughApp = () => {
    if (!canClaim || !redeem(selectedPoints)) return;

    selectedUnits.forEach((reward) =>
      addLine({
        dishId: reward.dish.id,
        translations: reward.dish.translations,
        // Claimed with points, so it costs nothing on the bill.
        basePrice: 0,
        imageUrl: reward.dish.image?.url,
        toppings: [],
        quantity: 1,
        isReward: true,
      }),
    );

    clearSelection();
    navigate(orderPaths(restaurantId, tableId).cart);
  };

  if (pending && showCode) {
    return (
      <Grid>
        <div className={`start-1 col-4 ${styles.loyaltyWrapper}`}>
          <div className={styles.loyaltyHeaderContainer}>
            <div className={styles.loyaltyHeader}>
              <IconButton
                iconName="las la-angle-left icon-big"
                onClick={() => setShowCode(false)}
                color="primary"
                variant="text"
              />
              <p className={`body-bold-large ${styles.loyaltyTitle}`}>
                {t("loyalty.redeemedTitle")}
              </p>
              <IconButton iconName="" onClick={() => {}} color="primary" variant="text" />
            </div>
          </div>
          <div className={styles.loyaltyContent}>
            <LoyaltyQrCode value={pending.code} />
            <p className={`body-medium ${styles.loyaltyHint}`}>
              {t("loyalty.redeemedHint", { minutes: minutesLeft })}
            </p>
            <Button
              onClick={settleRedemption}
              label={t("loyalty.button.settle")}
              variant="contained"
              color="primary"
              maxWidth
              mobile={true}
            />
            <Button
              onClick={cancelRedemption}
              label={t("loyalty.button.cancel")}
              variant="outlined"
              color="primary"
              maxWidth
              mobile={true}
            />
          </div>
        </div>
      </Grid>
    );
  }

  return (
    <>
      <Grid>
      <div className={`start-1 col-4 ${styles.loyaltyWrapper}`}>
        <div className={styles.loyaltyHeaderContainer}>
          <div className={styles.loyaltyHeader}>
            <IconButton
              iconName="las la-angle-left icon-big"
              onClick={goBack}
              color="primary"
              variant="text"
            />
            <p className={`body-bold-large ${styles.loyaltyTitle}`}>
              {t("loyalty.title")}
            </p>
            {/* Signing in lives on the account page now, so this is only the
                spacer that keeps the title centred. */}
            <IconButton
              iconName=""
              onClick={() => {}}
              color="primary"
              variant="text"
            />
          </div>
        </div>

        <div className={styles.loyaltyContent}>
          <TransparentTile>
            <>
              <p className={`body-medium ${styles.balanceLabel}`}>
                {t("loyalty.balance")}
              </p>
              <p className={`headline-medium ${styles.balanceValue}`}>
                {points}
              </p>
            </>
          </TransparentTile>

          <p className={`body-medium ${styles.loyaltyHint}`}>
            {points === 0 ? t("loyalty.empty") : t("loyalty.howItWorks")}
          </p>

          {rewards.length === 0 ? (
            <p className={`body-medium ${styles.loyaltyHint}`}>
              {t("loyalty.noRewards")}
            </p>
          ) : null}

          <div className={styles.rewardList}>
            {rewards.map((reward) => (
              <ItemToPayComponent
                key={`${reward.id}-${listVersion}`}
                description={getTranslation(reward.dish.translations, language)}
                price={t("loyalty.cost", { count: reward.points })}
                quantity={quantities[reward.id] ?? 0}
                onQuantityChange={(quantity) =>
                  setQuantity(reward.id, quantity)
                }
                maxQuantity={maxQuantityFor(reward)}
                onDetails={() =>
                  navigate(menuDishPath(restaurantId, reward.dish.id))
                }
              />
            ))}
          </div>
        </div>
      </div>
      </Grid>

      {rewards.length > 0 ? (
        <OrderActionBar
          summaryLabel={t("loyalty.selected", { count: selectedPoints })}
          summaryValue={t("loyalty.cost", { count: availablePoints })}
        >
          <>
            <Button
              onClick={handleOrderThroughApp}
              label={t("loyalty.button.orderThroughApp")}
              variant="contained"
              color="primary"
              maxWidth
              mobile={true}
              disabled={!canClaim}
            />
            <Button
              onClick={handleGenerateCode}
              label={t("loyalty.button.generateCode")}
              variant="outlined"
              color="primary"
              maxWidth
              mobile={true}
              disabled={!canClaim}
            />
          </>
        </OrderActionBar>
      ) : null}
    </>
  );
}

export default LoyaltyPage;
